'use client';

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { format, subDays, startOfMonth, endOfMonth, isWithinInterval, isAfter, isBefore } from 'date-fns';
import type { 
  Transaction, 
  TransactionFilters,
  TransactionStats,
  TransactionGroup,
  TransactionStatus,
  TransactionType,
  Category,
  Merchant
} from '@/types/transaction';

// Export categories for use in components
export const TRANSACTION_CATEGORIES: Category[] = [
  { id: 'food', name: 'Food & Dining', icon: '🍽️', color: 'bg-amber-100 text-amber-800' },
  { id: 'shopping', name: 'Shopping', icon: '🛍️', color: 'bg-blue-100 text-blue-800' },
  { id: 'transportation', name: 'Transportation', icon: '🚗', color: 'bg-emerald-100 text-emerald-800' },
  { id: 'bills', name: 'Bills & Utilities', icon: '💡', color: 'bg-purple-100 text-purple-800' },
  { id: 'entertainment', name: 'Entertainment', icon: '🎬', color: 'bg-pink-100 text-pink-800' },
  { id: 'income', name: 'Income', icon: '💰', color: 'bg-green-100 text-green-800' },
  { id: 'transfer', name: 'Transfer', icon: '🔄', color: 'bg-gray-100 text-gray-800' },
  { id: 'other', name: 'Other', icon: '📌', color: 'bg-gray-100 text-gray-800' },
];

// Mock merchants
const MOCK_MERCHANTS: Merchant[] = [
  { id: 'amazon', name: 'Amazon', categoryId: 'shopping' },
  { id: 'starbucks', name: 'Starbucks', categoryId: 'food' },
  { id: 'uber', name: 'Uber', categoryId: 'transportation' },
  { id: 'netflix', name: 'Netflix', categoryId: 'entertainment' },
  { id: 'spotify', name: 'Spotify', categoryId: 'entertainment' },
  { id: 'wholefoods', name: 'Whole Foods', categoryId: 'food' },
  { id: 'shell', name: 'Shell', categoryId: 'transportation' },
];

// Generate mock transactions
const generateMockTransactions = (count: number = 100): Transaction[] => {
  const transactions: Transaction[] = [];
  const statuses: TransactionStatus[] = ['completed', 'pending', 'cancelled'];
  const types: TransactionType[] = ['expense', 'income', 'transfer'];
  
  for (let i = 0; i < count; i++) {
    const isExpense = Math.random() > 0.3;
    const isTransfer = !isExpense && Math.random() > 0.5;
    const type = isTransfer ? 'transfer' : isExpense ? 'expense' : 'income';
    const amount = isExpense 
      ? Math.round((Math.random() * 500 + 5) * 100) / 100
      : isTransfer
      ? Math.round((Math.random() * 2000 + 100) * 100) / 100
      : Math.round((Math.random() * 5000 + 1000) * 100) / 100;
    
    const daysAgo = Math.floor(Math.random() * 90);
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    
    const merchant = MOCK_MERCHANTS[Math.floor(Math.random() * MOCK_MERCHANTS.length)];
    const category = TRANSACTION_CATEGORIES.find(cat => cat.id === (merchant?.categoryId || 'other')) || TRANSACTION_CATEGORIES[TRANSACTION_CATEGORIES.length - 1];
    
    transactions.push({
      id: `tx_${i + 1}`,
      accountId: `acc_${Math.floor(Math.random() * 3) + 1}`,
      accountName: ['Chase', 'Wells Fargo', 'Bank of America'][Math.floor(Math.random() * 3)],
      amount: type === 'expense' ? -Math.abs(amount) : Math.abs(amount),
      currency: 'USD',
      date: date.toISOString(),
      description: merchant ? `${merchant.name} Purchase` : `Transaction #${i + 1}`,
      merchant,
      category,
      status: statuses[Math.floor(Math.random() * statuses.length)] as TransactionStatus,
      type: type as TransactionType,
      isRecurring: Math.random() > 0.8,
      needsReview: Math.random() > 0.7,
      createdAt: date.toISOString(),
      updatedAt: date.toISOString(),
    });
  }
  
  return transactions;
};

// Calculate statistics from transactions
const calculateStats = (transactions: Transaction[]): TransactionStats => {
  if (transactions.length === 0) {
    return {
      totalSpent: 0,
      largestPurchase: null,
      topCategory: null,
      income: 0,
      expenses: 0,
      net: 0
    };
  }

  const expenses = transactions.filter(tx => tx.amount < 0);
  const income = transactions.filter(tx => tx.amount > 0 && tx.type === 'income');
  
  const totalExpenses = Math.abs(expenses.reduce((sum, tx) => sum + tx.amount, 0));
  const totalIncome = income.reduce((sum, tx) => sum + tx.amount, 0);
  const net = totalIncome - totalExpenses;
  
  // Find largest purchase (most expensive expense)
  const largestPurchase = expenses.length > 0 
    ? [...expenses].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))[0]
    : null;

  // Calculate spending by category
  const categorySpending: Record<string, { amount: number; count: number }> = {};
  
  expenses.forEach(tx => {
    if (tx.category) {
      if (!categorySpending[tx.category.id]) {
        categorySpending[tx.category.id] = { amount: 0, count: 0 };
      }
      categorySpending[tx.category.id].amount += Math.abs(tx.amount);
      categorySpending[tx.category.id].count++;
    }
  });

  // Find top category
  let topCategory = null;
  const categoryEntries = Object.entries(categorySpending);
  
  if (categoryEntries.length > 0) {
    const [topCategoryId, data] = categoryEntries.reduce(
      (max, [id, catData]) => catData.amount > max[1].amount 
        ? [id, catData] 
        : max,
      ['', { amount: 0, count: 0 }]
    );
    
    const category = TRANSACTION_CATEGORIES.find(c => c.id === topCategoryId);
    if (category) {
      topCategory = {
        name: category.name,
        amount: data.amount,
        percentage: (data.amount / totalExpenses) * 100
      };
    }
  }

  return {
    totalSpent: totalExpenses,
    largestPurchase,
    topCategory,
    income: totalIncome,
    expenses: totalExpenses,
    net
  };
};

interface TransactionsContextType {
  transactions: Transaction[];
  filteredTransactions: Transaction[];
  stats: TransactionStats;
  filters: TransactionFilters;
  categories: Category[];
  isLoading: boolean;
  error: string | null;
  updateFilters: (updates: Partial<TransactionFilters>) => void;
  resetFilters: () => void;
  categorizeTransaction: (transactionId: string, categoryId: string) => void;
  groupTransactionsByDate: () => TransactionGroup[];
  refetch: () => Promise<void>;
}

const defaultFilters: TransactionFilters = {
  searchQuery: '',
  accounts: [],
  categories: [],
  dateRange: {
    from: undefined,
    to: undefined,
  },
  amountRange: {
    min: '',
    max: '',
  },
  needsAttention: false,
  status: [],
  type: [],
};

// Re-export types for backward compatibility
export type { 
  Transaction, 
  TransactionFilters,
  TransactionStats,
  TransactionGroup,
  TransactionStatus,
  TransactionType,
  Category,
  Merchant
} from '@/types/transaction';

const TransactionsContext = createContext<TransactionsContextType | undefined>(undefined);

export function TransactionsProvider({ children }: { children: React.ReactNode }) {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [filters, setFilters] = useState<TransactionFilters>(defaultFilters);
  const [stats, setStats] = useState<TransactionStats>({
    totalSpent: 0,
    largestPurchase: null,
    topCategory: null,
    income: 0,
    expenses: 0,
    net: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Apply filters to transactions
  const applyFilters = useCallback((txs: Transaction[], filterValues: TransactionFilters): Transaction[] => {
    return txs.filter(tx => {
      // Search query
      if (filterValues.searchQuery) {
        const searchLower = filterValues.searchQuery.toLowerCase();
        const matchesSearch = 
          tx.description.toLowerCase().includes(searchLower) ||
          (tx.merchant?.name?.toLowerCase().includes(searchLower) ?? false) ||
          (tx.category?.name.toLowerCase().includes(searchLower) ?? false);
        
        if (!matchesSearch) return false;
      }
      
      // Accounts filter
      if (filterValues.accounts.length > 0 && !filterValues.accounts.includes(tx.accountId)) {
        return false;
      }
      
      // Categories filter
      if (filterValues.categories.length > 0 && (!tx.category || !filterValues.categories.includes(tx.category.id))) {
        return false;
      }
      
      // Date range filter
      if (filterValues.dateRange.from || filterValues.dateRange.to) {
        const txDate = new Date(tx.date);
        const from = filterValues.dateRange.from ? new Date(filterValues.dateRange.from) : null;
        const to = filterValues.dateRange.to ? new Date(filterValues.dateRange.to) : null;
        
        if (from && txDate < from) return false;
        if (to && txDate > to) return false;
      }
      
      // Amount range filter
      if (filterValues.amountRange.min !== '' || filterValues.amountRange.max !== '') {
        const min = filterValues.amountRange.min !== '' ? Number(filterValues.amountRange.min) : -Infinity;
        const max = filterValues.amountRange.max !== '' ? Number(filterValues.amountRange.max) : Infinity;
        const amount = Math.abs(tx.amount);
        
        if (amount < min || amount > max) return false;
      }
      
      // Needs attention filter
      if (filterValues.needsAttention && !tx.needsReview) {
        return false;
      }
      
      // Status filter
      if (filterValues.status.length > 0 && !filterValues.status.includes(tx.status)) {
        return false;
      }
      
      // Type filter
      if (filterValues.type.length > 0 && !filterValues.type.includes(tx.type)) {
        return false;
      }
      
      return true;
    });
  }, []);

  // Group transactions by date
  const groupTransactionsByDate = useCallback((): TransactionGroup[] => {
    const groups: Record<string, Transaction[]> = {};
    
    filteredTransactions.forEach(tx => {
      const date = new Date(tx.date).toISOString().split('T')[0];
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(tx);
    });
    
    return Object.entries(groups)
      .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
      .map(([date, txs]) => ({
        date,
        transactions: txs.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
        total: txs.reduce((sum, tx) => sum + tx.amount, 0)
      }));
  }, [filteredTransactions]);

  // Categorize a transaction
  const categorizeTransaction = useCallback((transactionId: string, categoryId: string) => {
    setTransactions(prev => 
      prev.map(tx => {
        if (tx.id === transactionId) {
          const category = TRANSACTION_CATEGORIES.find(c => c.id === categoryId);
          return { ...tx, category, needsReview: false };
        }
        return tx;
      })
    );
    
    // Update filtered transactions as well
    setFilteredTransactions(prev => 
      prev.map(tx => {
        if (tx.id === transactionId) {
          const category = TRANSACTION_CATEGORIES.find(c => c.id === categoryId);
          return { ...tx, category, needsReview: false };
        }
        return tx;
      })
    );
  }, []);

  // Fetch transactions (simulated)
  const fetchTransactions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Generate mock data
      const mockData = generateMockTransactions(150);
      setTransactions(mockData);
      
      // Apply initial filters
      const filtered = applyFilters(mockData, filters);
      setFilteredTransactions(filtered);
      
      // Calculate stats
      setStats(calculateStats(filtered));
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
      setError('Failed to load transactions. Please try again.');
      setTransactions([]);
      setFilteredTransactions([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters, applyFilters]);

  // Update filters
  const updateFilters = useCallback((updates: Partial<TransactionFilters>) => {
    setFilters(prev => {
      const newFilters = { ...prev, ...updates };
      
      // Apply filters to transactions
      const filtered = applyFilters(transactions, newFilters);
      setFilteredTransactions(filtered);
      
      // Update stats
      setStats(calculateStats(filtered));
      
      return newFilters;
    });
  }, [transactions, applyFilters]);

  // Reset filters
  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
    
    // Reset filtered transactions to all transactions
    setFilteredTransactions(transactions);
    
    // Update stats
    setStats(calculateStats(transactions));
  }, [transactions]);

  // Initial data fetch
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Context value
  const value = useMemo(() => ({
    transactions,
    filteredTransactions,
    stats,
    filters,
    categories: TRANSACTION_CATEGORIES,
    isLoading,
    error,
    updateFilters,
    resetFilters,
    categorizeTransaction,
    groupTransactionsByDate,
    refetch: fetchTransactions,
  }), [
    transactions,
    filteredTransactions,
    stats,
    filters,
    isLoading,
    error,
    updateFilters,
    resetFilters,
    categorizeTransaction,
    groupTransactionsByDate,
    fetchTransactions,
  ]);

  return (
    <TransactionsContext.Provider value={value}>
      {children}
    </TransactionsContext.Provider>
  );
}

export function useTransactions() {
  const context = useContext(TransactionsContext);
  if (context === undefined) {
    throw new Error('useTransactions must be used within a TransactionsProvider');
  }
  return context;
}
